import { useState, type ReactNode } from 'react';
import { ExternalLink, Globe, Layers, List, Lock, Network, Pencil, Route } from 'lucide-react';
import { Button, Card, EmptyState, StatusPill, SubTabs, TabPanel, type TabDefinition } from '@/components/ui';
import { DomainsDialog } from '@/components/sites/DomainsDialog';
import { ZoneSections, type ZoneSection } from '@/components/sites/dns/ZoneSections';
import { ZoneSsl } from '@/components/sites/ZoneSsl';
import { formatRelative } from '@/utils/format';
import { DOMAIN_ROLE_LABELS, DOMAIN_STATE, label } from '@/utils/labels';
import { siteUrl } from '@/utils/siteUrl';
import type { SiteDetail } from '@/types/api';

type Section = 'names' | ZoneSection;

const SECTIONS: readonly TabDefinition<Section>[] = [
  { id: 'names', label: 'Домены', icon: Globe },
  { id: 'records', label: 'DNS-записи', icon: List },
  { id: 'ssl', label: 'SSL и HTTPS', icon: Lock },
  { id: 'redirects', label: 'Редиректы', icon: Route },
  { id: 'cache', label: 'Кэш', icon: Layers },
  { id: 'ns', label: 'NS и регистратор', icon: Network },
];

const PREFIX = 'domains-';

/**
 * A site's names and their domains' settings, one part at a time: the names
 * themselves, then DNS records, SSL, redirects, cache and name servers of the
 * registered domain they live under (ADR-0032).
 */
export function DomainsTab({
  site,
  mayEdit,
  mayManageDns,
}: {
  readonly site: SiteDetail;
  readonly mayEdit: boolean;
  /** DNS records and Cloudflare settings through 2d2s (ADR-0032). */
  readonly mayManageDns: boolean;
}): ReactNode {
  const [section, setSection] = useState<Section>('names');

  if (site.domains.length === 0) {
    return (
      <Card flush>
        <EmptyState icon={Globe} message="К сайту не привязан ни один домен" />
      </Card>
    );
  }

  return (
    <div className="stack">
      <div>
        <SubTabs tabs={SECTIONS} active={section} onChange={setSection} label="Настройки доменов" prefix={PREFIX} />
      </div>
      <TabPanel id={section} active={section} prefix={PREFIX}>
        {section === 'names' ? <SiteNames site={site} mayEdit={mayEdit} /> : <ZoneSections siteId={site.id} section={section} mayManage={mayManageDns} />}
      </TabPanel>
    </div>
  );
}

/** The site's names: where each should point, and where it does. */
function SiteNames({ site, mayEdit }: { readonly site: SiteDetail; readonly mayEdit: boolean }): ReactNode {
  const [editing, setEditing] = useState(false);

  return (
    <Card
      flush
      title="Домены сайта"
      icon={Globe}
      actions={
        mayEdit ? (
          <Button size="sm" icon={Pencil} onClick={() => setEditing(true)}>
            Изменить домены
          </Button>
        ) : null
      }
    >
      {mayEdit ? <DomainsDialog site={site} open={editing} onClose={() => setEditing(false)} /> : null}
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th scope="col">Домен</th>
              <th scope="col">Роль</th>
              <th scope="col">Состояние</th>
              <th scope="col">Должен указывать на</th>
              <th scope="col">Указывает на</th>
              <th scope="col">SSL зоны</th>
              <th scope="col">Проверен</th>
            </tr>
          </thead>
          <tbody>
            {site.domains.map((domain) => {
              // Drift is the thing this table exists to show: a domain whose
              // real record no longer matches the one the panel intends.
              const drift = domain.desiredIpv4 !== null && domain.actualIpv4 !== null && domain.desiredIpv4 !== domain.actualIpv4;

              return (
                <tr key={domain.id}>
                  <td>
                    <a className="mono link-external" href={siteUrl(domain.fqdn)} target="_blank" rel="noreferrer noopener">
                      {domain.fqdn}
                      <ExternalLink size={12} aria-hidden="true" />
                    </a>
                  </td>
                  <td className="nowrap">{label(DOMAIN_ROLE_LABELS, domain.role)}</td>
                  <td>
                    {/* Measured drift wins over the stored state: the stored
                        value is what we last decided, the addresses beside it
                        are what is true now. */}
                    <div className="stack stack-tight">
                      <StatusPill map={DOMAIN_STATE} status={drift ? 'drift' : domain.state} />
                      {domain.errorCode === 'AWAITING_SWITCH' ? (
                        <span className="text-sm muted">старый сервер до переключения импорта</span>
                      ) : null}
                      {domain.errorCode === 'DNS_NOT_MANAGED' ? (
                        // Outside the domains the panel may write in 2d2s: said
                        // plainly, so nobody waits for it to change by itself.
                        <span className="text-sm muted">запись DNS меняется вручную в 2d2s</span>
                      ) : null}
                    </div>
                  </td>
                  <td className="mono muted">{domain.desiredIpv4 ?? '—'}</td>
                  <td className={`mono ${drift ? 'tone-down' : 'muted'}`}>{domain.actualIpv4 ?? '—'}</td>
                  <td className="nowrap">
                    <ZoneSsl mode={domain.zoneSslMode} authenticated={site.originCertificate?.authenticated ?? false} />
                  </td>
                  <td className="muted nowrap">{formatRelative(domain.lastCheckedAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
